import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { myContactsApi } from "../components/myContacts/api/myContactsApi";

const EMPTY_FORM = { name: "", email: "", phone: "", company: "", category: "", notes: "" };

function StatCard({ label, value }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E5E7EB", flex: 1, minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 600, mt: 0.5 }}>
        {Number(value || 0).toLocaleString()}
      </Typography>
    </Paper>
  );
}

export default function MyContactsPage() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(async () => {
    try { setStats(await myContactsApi.stats()); } catch { /* non-fatal */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await myContactsApi.list({ q });
      setRows(data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load contacts.");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(loadList, 300);
    return () => clearTimeout(t);
  }, [loadList]);

  const openAdd = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "", email: row.email || "", phone: row.phone || "",
      company: row.company || "", category: row.category || "", notes: row.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (editingId) await myContactsApi.update(editingId, form);
      else await myContactsApi.create(form);
      setDialogOpen(false);
      await Promise.all([loadList(), loadStats()]);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save the contact.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ${row.name || row.email}?`)) return;
    try {
      await myContactsApi.remove(row.id);
      await Promise.all([loadList(), loadStats()]);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not delete the contact.");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const res = await myContactsApi.import(file);
      const c = res?.counts || {};
      setError(`Imported: ${c.created || 0} created, ${c.updated || 0} updated, ${c.skipped_invalid || 0} skipped.`);
      await Promise.all([loadList(), loadStats()]);
    } catch (err) {
      setError(err?.response?.data?.message || "Import failed. Use a CSV with an email column.");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>My Contacts</Typography>
        <Stack direction="row" spacing={1}>
          <Button component="label" startIcon={<UploadIcon />} sx={{ textTransform: "none" }}>
            Import CSV/Excel
            <input type="file" accept=".csv,.txt" hidden onChange={handleImport} />
          </Button>
          <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={openAdd} sx={{ textTransform: "none", fontWeight: 700 }}>
            Add contact
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <StatCard label="Total" value={stats?.list?.total} />
        <StatCard label="Added (30d)" value={stats?.list?.added_30d} />
        <StatCard label="Emailed" value={stats?.reach?.emailed} />
        <StatCard label="Opens" value={stats?.engagement?.opens} />
      </Stack>

      {error ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert> : null}

      <TextField
        size="small" placeholder="Search name, email or company"
        value={q} onChange={(e) => setQ(e.target.value)} fullWidth sx={{ mb: 2 }}
      />

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No contacts yet. Add one or import a CSV.
                </TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phone || "—"}</TableCell>
                  <TableCell>{row.company || "—"}</TableCell>
                  <TableCell>{row.category ? <Chip size="small" label={row.category} /> : "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? "Edit contact" : "Add contact"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth size="small" />
            <TextField label="Email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} fullWidth size="small" />
            <TextField label="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} fullWidth size="small" />
            <TextField label="Company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} fullWidth size="small" />
            <TextField select label="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} fullWidth size="small">
              <MenuItem value="">None</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="vip">VIP</MenuItem>
              <MenuItem value="prospect">Prospect</MenuItem>
            </TextField>
            <TextField label="Notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} fullWidth size="small" multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleSave} disabled={saving || !form.email} sx={{ textTransform: "none", fontWeight: 700 }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
